import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Play, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  X,
  Lock,
  Target,
  Heart,
  Briefcase,
  Users,
  Leaf,
  Star,
  Timer,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Award,
  Flame,
  BookOpen
} from 'lucide-react';

const CATEGORY_CONFIG = {
  abundance: { icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Abundance' },
  health: { icon: Heart, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Health' },
  relationships: { icon: Users, color: 'text-pink-400', bg: 'bg-pink-500/20', label: 'Relationships' },
  career: { icon: Briefcase, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Career' },
  self_growth: { icon: Leaf, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Self Growth' },
  peace: { icon: Sparkles, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Peace' }
};

const TYPE_LABELS = {
  visualization: 'Visualization',
  affirmation: 'Affirmation',
  meditation: 'Meditation',
  scripting: 'Scripting',
  gratitude: 'Gratitude',
  quantum_jumping: 'Quantum Jumping'
};

export const QuantumManifestation = () => {
  const { user } = useAuth();
  const [techniques, setTechniques] = useState([]);
  const [intentions, setIntentions] = useState([]);
  const [practiceHistory, setPracticeHistory] = useState({ practices: [], stats: {}, current_streak: 0 });
  const [dailyGuidance, setDailyGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('practices'); // 'practices', 'intentions', 'history'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showTechniqueModal, setShowTechniqueModal] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [practiceStep, setPracticeStep] = useState(0);
  const [isPracticing, setIsPracticing] = useState(false);
  const [stepTimer, setStepTimer] = useState(0);
  const [showIntentionModal, setShowIntentionModal] = useState(false);
  const [intentionForm, setIntentionForm] = useState({
    title: '',
    description: '',
    category: 'abundance',
    target_date: '',
    affirmations: ['']
  });

  const isPro = user?.role === 'admin' || user?.role === 'superuser' || user?.subscription_status === 'active';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [techRes, intentRes, histRes, guideRes] = await Promise.all([
        api.get('/quantum/techniques'),
        api.get('/quantum/intentions'),
        api.get('/quantum/practice/history'),
        api.get('/quantum/daily-guidance')
      ]);
      setTechniques(techRes.data.techniques || []);
      setIntentions(intentRes.data.intentions || []);
      setPracticeHistory(histRes.data);
      setDailyGuidance(guideRes.data);
    } catch (error) {
      console.error('Failed to fetch quantum data:', error);
      toast.error('Failed to load quantum manifestation data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Step timer
  useEffect(() => {
    let interval;
    if (isPracticing && selectedTechnique && practiceStep < selectedTechnique.steps.length) {
      const stepDuration = selectedTechnique.steps[practiceStep]?.duration || 60;
      setStepTimer(stepDuration);
      interval = setInterval(() => {
        setStepTimer(prev => {
          if (prev <= 1) {
            // Auto advance to next step
            if (practiceStep < selectedTechnique.steps.length - 1) {
              setPracticeStep(p => p + 1);
            }
            return stepDuration;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPracticing, practiceStep, selectedTechnique]);

  const startPractice = (technique) => {
    if (!technique.is_accessible) {
      toast.error('Upgrade to Pro to access this technique');
      return;
    }
    setSelectedTechnique(technique);
    setPracticeStep(0);
    setIsPracticing(false);
    setShowTechniqueModal(true);
  };

  const beginPractice = () => {
    setIsPracticing(true);
    setPracticeStep(0);
  };

  const completePractice = async (feelingBefore, feelingAfter) => {
    try {
      await api.post('/quantum/practice', {
        technique_id: selectedTechnique.id,
        duration_minutes: selectedTechnique.duration_minutes,
        feeling_before: feelingBefore,
        feeling_after: feelingAfter
      });
      toast.success('Practice completed! Great work manifesting your reality.');
      setShowTechniqueModal(false);
      setSelectedTechnique(null);
      setIsPracticing(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to log practice');
    }
  };

  const createIntention = async () => {
    if (!intentionForm.title || !intentionForm.description) {
      toast.error('Please fill in title and description');
      return;
    }
    try {
      const payload = {
        ...intentionForm,
        affirmations: intentionForm.affirmations.filter(a => a.trim())
      };
      await api.post('/quantum/intentions', payload);
      toast.success('Intention created! The universe is listening.');
      setShowIntentionModal(false);
      setIntentionForm({ title: '', description: '', category: 'abundance', target_date: '', affirmations: [''] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create intention');
    }
  };

  const markManifested = async (intentionId) => {
    try {
      await api.put(`/quantum/intentions/${intentionId}`, { is_manifested: true });
      toast.success('Congratulations! You manifested your desire!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update intention');
    }
  };

  const deleteIntention = async (intentionId) => {
    try {
      await api.delete(`/quantum/intentions/${intentionId}`);
      toast.success('Intention deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete intention');
    }
  };

  const filteredTechniques = selectedCategory === 'all' 
    ? techniques 
    : techniques.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-purple-400 animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Loading quantum field...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="quantum-manifestation-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20">
              <Sparkles className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Guided Quantum Manifestation</h1>
              <p className="text-muted-foreground">Harness the power of quantum physics to manifest your desires</p>
            </div>
          </div>
        </div>

        {/* Daily Guidance Card */}
        {dailyGuidance && (
          <div className="mb-8 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">{dailyGuidance.greeting}</h2>
                <p className="text-purple-300 italic mb-4">"{dailyGuidance.daily_affirmation}"</p>
                <p className="text-sm text-muted-foreground mb-3">{dailyGuidance.manifestation_tip}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Flame className="w-4 h-4 text-orange-400" />
                    <span className="text-foreground">{dailyGuidance.streak_message}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-purple-400">{practiceHistory.current_streak}</div>
                <div className="text-xs text-muted-foreground">Day Streak</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <BookOpen className="w-4 h-4" />
              <span>Total Practices</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{practiceHistory.stats?.total_practices || 0}</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Timer className="w-4 h-4" />
              <span>Minutes Practiced</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{practiceHistory.stats?.total_minutes || 0}</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Target className="w-4 h-4" />
              <span>Active Intentions</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{intentions.length}</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Award className="w-4 h-4" />
              <span>Techniques</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{techniques.length}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 p-1 bg-muted/50 rounded-xl w-fit">
          {['practices', 'intentions', 'history'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all capitalize ${
                activeTab === tab
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Practices Tab */}
        {activeTab === 'practices' && (
          <>
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-purple-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                All Techniques
              </button>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                const IconComp = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedCategory === key
                        ? `${config.bg} ${config.color}`
                        : 'bg-muted text-muted-foreground hover:bg-muted/70'
                    }`}
                  >
                    <IconComp className="w-4 h-4" />
                    {config.label}
                  </button>
                );
              })}
            </div>

            {/* Techniques Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTechniques.map(technique => {
                const catConfig = CATEGORY_CONFIG[technique.category] || CATEGORY_CONFIG.abundance;
                const CatIcon = catConfig.icon;
                return (
                  <div
                    key={technique.id}
                    onClick={() => startPractice(technique)}
                    className={`bg-card rounded-2xl p-5 border border-border cursor-pointer hover:border-purple-500/50 transition-all ${
                      !technique.is_accessible ? 'opacity-60' : ''
                    }`}
                    data-testid={`technique-${technique.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-xl ${catConfig.bg}`}>
                        <CatIcon className={`w-5 h-5 ${catConfig.color}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        {technique.is_premium && !technique.is_accessible && (
                          <Lock className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-xs text-muted-foreground">{technique.duration_minutes}m</span>
                      </div>
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{technique.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{technique.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground">
                        {TYPE_LABELS[technique.type]}
                      </span>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Intentions Tab */}
        {activeTab === 'intentions' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-foreground">Your Manifestation Intentions</h2>
              <button
                onClick={() => setShowIntentionModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors"
                data-testid="add-intention-btn"
              >
                <Plus className="w-5 h-5" />
                New Intention
              </button>
            </div>

            {intentions.length === 0 ? (
              <div className="text-center py-16">
                <Target className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">No Intentions Yet</h3>
                <p className="text-muted-foreground mb-4">Set your first manifestation intention to begin your quantum journey</p>
                <button
                  onClick={() => setShowIntentionModal(true)}
                  className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600"
                >
                  Create First Intention
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {intentions.map(intention => {
                  const catConfig = CATEGORY_CONFIG[intention.category] || CATEGORY_CONFIG.abundance;
                  const CatIcon = catConfig.icon;
                  return (
                    <div key={intention.id} className="bg-card rounded-2xl p-5 border border-border">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`p-2 rounded-xl ${catConfig.bg}`}>
                          <CatIcon className={`w-5 h-5 ${catConfig.color}`} />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => markManifested(intention.id)}
                            className="p-1.5 rounded-lg hover:bg-green-500/20 text-green-400 transition-colors"
                            title="Mark as manifested"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteIntention(intention.id)}
                            className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <h3 className="font-bold text-foreground mb-1">{intention.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{intention.description}</p>
                      {intention.target_date && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                          <Calendar className="w-3 h-3" />
                          Target: {new Date(intention.target_date).toLocaleDateString()}
                        </div>
                      )}
                      {intention.affirmations?.length > 0 && (
                        <div className="space-y-1">
                          {intention.affirmations.slice(0, 2).map((aff, idx) => (
                            <p key={idx} className="text-xs text-purple-400 italic">"{aff}"</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <>
            <h2 className="text-xl font-bold text-foreground mb-6">Practice History (Last 30 Days)</h2>
            {practiceHistory.practices?.length === 0 ? (
              <div className="text-center py-16">
                <Timer className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">No Practices Yet</h3>
                <p className="text-muted-foreground">Start practicing quantum manifestation techniques to build your history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {practiceHistory.practices.map(practice => (
                  <div key={practice.id} className="bg-card rounded-xl p-4 border border-border flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{practice.technique_title}</h4>
                        <p className="text-sm text-muted-foreground">{practice.technique_type} • {practice.duration_minutes} min</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-foreground">
                        {new Date(practice.completed_at).toLocaleDateString()}
                      </div>
                      {practice.feeling_before && practice.feeling_after && (
                        <div className="text-xs text-muted-foreground">
                          Feeling: {practice.feeling_before} → {practice.feeling_after}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Technique Practice Modal */}
      {showTechniqueModal && selectedTechnique && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-lg overflow-hidden">
            {!isPracticing ? (
              // Technique Info
              <>
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-foreground">{selectedTechnique.title}</h2>
                    <button onClick={() => setShowTechniqueModal(false)} className="text-muted-foreground hover:text-foreground">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-muted-foreground mb-6">{selectedTechnique.description}</p>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Timer className="w-4 h-4" />
                      {selectedTechnique.duration_minutes} minutes
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BookOpen className="w-4 h-4" />
                      {selectedTechnique.steps?.length || 0} steps
                    </div>
                  </div>
                  <button
                    onClick={beginPractice}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    data-testid="begin-practice-btn"
                  >
                    <Play className="w-5 h-5" />
                    Begin Practice
                  </button>
                </div>
              </>
            ) : practiceStep < selectedTechnique.steps.length ? (
              // Practice Steps
              <>
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-foreground">{selectedTechnique.title}</h2>
                    <button onClick={() => setShowTechniqueModal(false)} className="text-muted-foreground hover:text-foreground">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  {/* Progress */}
                  <div className="flex gap-1">
                    {selectedTechnique.steps.map((_, idx) => (
                      <div 
                        key={idx}
                        className={`flex-1 h-1.5 rounded-full transition-colors ${
                          idx <= practiceStep ? 'bg-purple-500' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                      <span className="text-3xl font-bold text-purple-400">{practiceStep + 1}</span>
                    </div>
                    <p className="text-lg text-foreground mb-6">{selectedTechnique.steps[practiceStep]?.instruction}</p>
                    <div className="text-2xl font-mono text-purple-400 mb-8">{stepTimer}s</div>
                    <div className="flex gap-3 justify-center">
                      {practiceStep > 0 && (
                        <button
                          onClick={() => setPracticeStep(p => p - 1)}
                          className="px-6 py-3 bg-muted text-foreground rounded-xl hover:bg-muted/70 flex items-center gap-2"
                        >
                          <ChevronLeft className="w-5 h-5" />
                          Back
                        </button>
                      )}
                      <button
                        onClick={() => setPracticeStep(p => p + 1)}
                        className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 flex items-center gap-2"
                        data-testid="next-step-btn"
                      >
                        {practiceStep < selectedTechnique.steps.length - 1 ? (
                          <>Next <ChevronRight className="w-5 h-5" /></>
                        ) : (
                          <>Complete <Check className="w-5 h-5" /></>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Completion & Feedback
              <>
                <div className="p-6 border-b border-border">
                  <h2 className="text-xl font-bold text-foreground text-center">Practice Complete!</h2>
                </div>
                <div className="p-6">
                  <div className="text-center py-4 mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                      <Check className="w-10 h-10 text-green-400" />
                    </div>
                    <p className="text-muted-foreground">You've completed your quantum manifestation practice</p>
                  </div>
                  <p className="text-sm text-muted-foreground text-center mb-4">How do you feel now compared to before?</p>
                  <div className="grid grid-cols-5 gap-2 mb-6">
                    {[1, 2, 3, 4, 5].map(feeling => (
                      <button
                        key={feeling}
                        onClick={() => completePractice(3, feeling)}
                        className="py-4 bg-muted rounded-xl hover:bg-purple-500/20 hover:text-purple-400 transition-all flex flex-col items-center gap-1"
                        data-testid={`feeling-${feeling}`}
                      >
                        <span className="text-2xl">{['😔', '😕', '😐', '🙂', '😊'][feeling - 1]}</span>
                        <span className="text-xs text-muted-foreground">{feeling}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Intention Modal */}
      {showIntentionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Create Intention</h2>
                <button onClick={() => setShowIntentionModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Title</label>
                <input
                  type="text"
                  value={intentionForm.title}
                  onChange={e => setIntentionForm({ ...intentionForm, title: e.target.value })}
                  placeholder="What do you want to manifest?"
                  className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  data-testid="intention-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  value={intentionForm.description}
                  onChange={e => setIntentionForm({ ...intentionForm, description: e.target.value })}
                  placeholder="Describe your intention in detail..."
                  rows={3}
                  className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                  data-testid="intention-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                    const IconComp = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setIntentionForm({ ...intentionForm, category: key })}
                        className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                          intentionForm.category === key
                            ? `${config.bg} border-current ${config.color}`
                            : 'border-border hover:border-muted-foreground'
                        }`}
                      >
                        <IconComp className="w-5 h-5" />
                        <span className="text-xs">{config.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Target Date (Optional)</label>
                <input
                  type="date"
                  value={intentionForm.target_date}
                  onChange={e => setIntentionForm({ ...intentionForm, target_date: e.target.value })}
                  className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Affirmations</label>
                {intentionForm.affirmations.map((aff, idx) => (
                  <input
                    key={idx}
                    type="text"
                    value={aff}
                    onChange={e => {
                      const newAffs = [...intentionForm.affirmations];
                      newAffs[idx] = e.target.value;
                      setIntentionForm({ ...intentionForm, affirmations: newAffs });
                    }}
                    placeholder={`Affirmation ${idx + 1}`}
                    className="w-full px-4 py-3 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 mb-2"
                  />
                ))}
                <button
                  onClick={() => setIntentionForm({ ...intentionForm, affirmations: [...intentionForm.affirmations, ''] })}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  + Add another affirmation
                </button>
              </div>
              <button
                onClick={createIntention}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity"
                data-testid="create-intention-submit"
              >
                Create Intention
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuantumManifestation;
